
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  ElementRef,
  forwardRef,
  inject,
  InjectionToken,
  Injector,
  input,
  OnInit,
  output,
  signal,
  ViewEncapsulation,
  WritableSignal,
  viewChild
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR, NgControl, ReactiveFormsModule } from '@angular/forms';
import { UiWarningIconDirective } from '../../icons/warning-icon.directive';
import { InputFieldErrorMessage } from './input-text.model';

type InputFieldType = 'text' | 'email' | 'password' | 'number' | 'tel' | 'url' | 'search';

export const INPUT_TEXT_COMPONENT = new InjectionToken<InputTextComponent>('InputTextComponent');

@Component({
  selector: 'ui-input-text',
  imports: [ReactiveFormsModule, UiWarningIconDirective],
  templateUrl: './input-text.component.html',
  styleUrls: ['../../_styles/index.scss', '../../_styles/primitives/inputs.scss', './input-text.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => InputTextComponent),
      multi: true,
    },
    { provide: INPUT_TEXT_COMPONENT, useExisting: InputTextComponent }
  ],
  encapsulation: ViewEncapsulation.ShadowDom,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class InputTextComponent implements ControlValueAccessor, OnInit {
  // Inputs
  readonly label = input.required<string>();
  readonly placeholder = input<string>('');
  readonly labelDisplay = input<'top' | 'left' | 'right' | 'hide'>('top');
  readonly additionalMessage = input<string>('');
  readonly type = input<InputFieldType>('text');
  readonly preventAutofilling = input<boolean>(false);
  readonly errorMessages = input<InputFieldErrorMessage>({ required: 'Required field.' });
  readonly errorMessagesPriority = input<string[]>([
    'required',
    'minlength',
    'maxlength',
    'min',
    'max',
    'pattern',
    'email',
  ]);

  // Outputs
  upKeyPressed = output<Event | KeyboardEvent>();
  downKeyPressed = output<Event | KeyboardEvent>();
  escKeyPressed = output<Event | KeyboardEvent>();
  focused = output<FocusEvent>();
  focusLost = output<FocusEvent>();

  // Injections
  private readonly injector = inject(Injector);

  // Generated attributes
  readonly id = crypto.randomUUID();
  readonly ariaId = crypto.randomUUID();

  // Signals
  protected value: WritableSignal<string> = signal('');
  private readonly hasLostFocus = signal<number>(0);
  isDisabled: WritableSignal<boolean> = signal(false);
  isDirty: WritableSignal<boolean> = signal(false);
  isTouched: WritableSignal<boolean> = signal(false);

  // Computed Signals
  readonly isInvalid = computed(() => {
    this.hasLostFocus(); // Track blur event
    this.value(); // Track value changes
    const control = this.ngControl?.control;

    return !!(control && control.invalid && control.touched);
  });

  readonly errorMessage = computed<string | undefined>(() => {
    const currentErrors = this.ngControl?.errors || {};

    if (!this.isInvalid()) return undefined;
    const currentErrorPrioritizedKey = Object.keys(currentErrors).sort((keyA, keyB) => {
      const priorityA =
        this.errorMessagesPriority().indexOf(keyA) >= 0
          ? this.errorMessagesPriority().indexOf(keyA)
          : this.errorMessagesPriority().length;
      const priorityB =
        this.errorMessagesPriority().indexOf(keyB) >= 0
          ? this.errorMessagesPriority().indexOf(keyB)
          : this.errorMessagesPriority().length;

      return priorityA - priorityB;
    })[0];

    return currentErrorPrioritizedKey && this.errorMessages()[currentErrorPrioritizedKey];
  });

  protected ngControl: NgControl | null = null;
  private onChange: (value: string) => void = () => {};
  private onTouched: () => void = () => {};

  readonly inputElement = viewChild<ElementRef<HTMLInputElement>>('inputElement');

  // Initialization
  ngOnInit(): void {
    // Manually hook up the CVA to the NgControl directive.
    // Doing this in ngOnInit to prevent a circular dependency between
    // NG_VALUE_ACCESSOR provider and NgControl injection happening in the same component.
    this.ngControl = this.injector.get(NgControl, null, { self: true, optional: true });
    if (this.ngControl) {
      this.ngControl.valueAccessor = this;
    }
  }

  // ControlValueAccessor Functions
  writeValue(value: any): void {
    // Ensure value is treated as a string for text input, handle null/undefined
    const newValue = value !== null && value !== undefined ? String(value) : '';
    this.value.set(newValue);
  }

  registerOnChange(fn: (value: any) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = () => {
      this.isTouched.set(true);
      fn();
    };
  }

  setDisabledState(isDisabled: boolean): void {
    this.isDisabled.set(isDisabled);
  }

  focus() {
    this.inputElement()?.nativeElement.focus();
  }

  // Event Handlers
  onInput(event: Event): void {
    const newValue = (event.target as HTMLInputElement).value;

    this.value.set(newValue);

    this.onChange(newValue);
  }

  onBlur(event: FocusEvent): void {
    this.onTouched();
    this.hasLostFocus.update(n => (n + 1) % 2);
    this.focusLost.emit(event);
  }
}
